/**
 * `/commission-runs` — the annual runs, and the statements of the one selected.
 *
 * Modelled on `CreditNotesPage` where the two ask the same question: the
 * loading / empty / error triad, URL-backed state written with `replace: true`,
 * one table from `md` with a stacked card list below it, and money as exact
 * decimal strings that never see `Number()`.
 *
 * ── SIX THINGS THIS PAGE EXISTS TO GET RIGHT ────────────────────────────────
 *
 * 1. 🔴 **IT DISCLOSES PAY, AND IT IS THE ONLY SURFACE IN THIS APP THAT DOES.**
 *    Owner decision 24, and backend migration 205 is the whole of it: the
 *    `commissions` module is granted to `management` and `super-admin` and to
 *    **nobody else** — not to `accounts`, which raises every invoice and every
 *    credit note in the business. ⛔ **Enforced on the SERVER**, by permission
 *    sets rather than by a role guard, so the controls here gate on `ability`
 *    and **never on `user.role`** — and the 403 is still handled, because the
 *    hidden control is the optimisation and the refusal is the guarantee. A
 *    role can be withdrawn under a live access token, which lasts days.
 *
 * 2. 🔴 **A DRAFT AND AN APPROVED RUN SAY DIFFERENT THINGS AND MUST LOOK
 *    DIFFERENT.** On a draft the revenue behind every figure is credited to the
 *    account lead **as resolved TODAY**, so moving one company to another lead
 *    rewrites the numbers on screen with nothing erroring; on an approved run
 *    the figures are frozen. `LIVE_ATTRIBUTION_NOTE` and
 *    `FROZEN_STATEMENT_NOTE` are the two sentences, and **which one renders is
 *    decided by the STATUS FLAGS, never by the status NAME** — those names are
 *    admin-editable labels, and this codebase has matched on one three times
 *    and been wrong three times (`"Unpaid"` contains `"paid"`).
 *
 *    ⚠️ **THE FREEZE IS AT COMPUTE, NOT AT APPROVAL, and that is the subtle
 *    half** (migration 204's own header). Approval makes a run
 *    UNRECOMPUTABLE; the figures were frozen when they were written. So a
 *    COMPUTED DRAFT carries the live-attribution note rather than the frozen
 *    one: what is on screen is a snapshot, and a recompute — the one thing
 *    that may legitimately move it — is still available.
 *
 * 3. ⛔ **`isApproved` AND `isVoid` ARE NOT COMPLEMENTS.** A draft is neither,
 *    and an approved run that is later voided is BOTH. `runState` below is the
 *    one place that ordering is written down, and it tests `isVoid` first —
 *    testing `isApproved` first paints a withdrawn run as live, which is the
 *    reassuring direction and therefore the one nobody reports.
 *
 * 4. 🔴 **THE EXCLUDED-DOCUMENT COUNTS ARE RENDERED, AND ARE NEVER `?? 0`.** A
 *    document with no exchange rate for its own bill date is **left out of the
 *    revenue and counted** — never converted at par — so a commission figure
 *    can be SMALLER than the revenue it represents and nothing else on this
 *    screen would say so. Zero is the healthy answer and is what production
 *    gives today, **which is exactly why a reader has to exist for it**: a
 *    figure that is only ever right is a figure nobody builds a reader for, and
 *    the day a rate gap appears a statement would otherwise under-pay somebody
 *    silently. `EXCLUDED_DOCUMENTS_NOTE` returns `null` at zero; the sentence
 *    renders when it does not. ⛔ On a field whose job is to report a GAP, a
 *    fallback IS the lie.
 *
 * 5. **`skipped` FROM A COMPUTE IS AN ORDINARY OUTCOME, NEVER AN ERROR.** An
 *    assignment that could not be computed is REPORTED rather than silently
 *    zeroed, because **a statement of 0.00 and a statement that could not be
 *    produced are different facts**. It renders in a neutral tone beside the
 *    figures, not as a danger block: a red panel on the ordinary case is the
 *    `skipped`-means-failure mistake this repo already records against the Xero
 *    push.
 *
 * 6. **APPROVAL IS IRREVERSIBLE IN EFFECT**, so it goes through `ConfirmDialog`
 *    naming the period — and saying the thing a reader cannot see: a credit
 *    raised later lands in **the year it was raised** rather than reopening
 *    this one.
 *
 * ── WHAT THIS PAGE DELIBERATELY DOES NOT DO ─────────────────────────────────
 *
 * ⛔ **NO TOTAL ACROSS STATEMENTS, AND IT IS NOT A GAP.** Every statement
 * carries its own `currency`, frozen from its scheme, so one run legitimately
 * holds several codes and a column sum would be wrong by construction rather
 * than merely imprecise. *SUPERSEDED 2026-10-25: "The figures are also PAGED"*
 * — both lists are now read whole so the column filters see every row (see
 * `LIST_PER_PAGE`), but a filtered view would make a footer total describe a
 * selection under a heading that implies the run. The count is the real
 * number; each figure renders with its code.
 *
 * ⛔ **NO SELF-SERVICE VIEW AND NONE IS TO BE BUILT** — migration 205 says so
 * in terms. There is no "my statement" route here and nothing on this page is
 * scoped to the reader.
 *
 * ⚠️ **NOTHING IS AUTO-SELECTED.** Arriving with no `?run=` shows a chooser
 * rather than the first row's statements: writing a run id into the URL on
 * arrival would make one link render differently for two people, and on a page
 * that discloses pay the URL should say exactly which year it is showing.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StatementPanel, StrandedRemaindersPanel } from '@/components/commissions';
import { PlusIcon } from '@/components/catalog/icons';
import { api, ApiError } from '@/lib/api';
import {
  attainmentTone,
  CLAWBACK_BROUGHT_FORWARD_LABEL,
  CLAWBACK_BROUGHT_FORWARD_SHORT,
  CLAWBACK_REFUNDED_LABEL,
  CLAWBACK_REFUNDED_SHORT,
  hasRefund,
  COMMISSION_VISIBILITY_NOTE,
  CREDIT_DOES_NOT_REDUCE_REVENUE_NOTE,
  EXCLUDED_DOCUMENTS_NOTE,
  formatPercent,
  FROZEN_STATEMENT_NOTE,
  gateSummary,
  hasBroughtForward,
  LIVE_ATTRIBUTION_NOTE,
  type AttainmentTone,
} from '@/lib/commissions';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime, formatDecimalString, toDateInputValue } from '@/lib/format';
import { denialReason, type ModuleSlug } from '@/lib/permissions';
import type {
  CommissionRun,
  CommissionRunComputeResult,
  CommissionRunListParams,
  CommissionRunStatus,
  CommissionStatement,
  CommissionStatementListParams,
  Paginated,
  TargetPeriodListParams,
  TargetPeriodRecord,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { usePageLayout } from '@/lib/use-page-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  type FrozenSide,
  Input,
  Modal,
  PageHeader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
  Textarea,
  ConfirmDialog,
  type BadgeTone,
} from '@/components/ui';
import { Money as UiMoney } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';

/**
 * ⛔ **THE SLUG IS SPELLED ONCE.** Every `ability` call and every
 * `denialReason` on this page reads it, and a second spelling would be a second
 * module — half the page gated on one and half on the other, with nothing
 * failing.
 *
 * ⚠️ **It must NEVER join `NAV_SURFACE_MODULE`.** That set forces
 * `create`/`update`/`delete` to a hard `false`, which would delete every
 * affordance this module exists to gate — and, worse, would leave a reader
 * believing the figures are protected by a view control when a view control
 * protects nothing. Migration 205 states this at length.
 */
const COMMISSIONS_MODULE: ModuleSlug = 'commissions';

/**
 * Both lists are read WHOLE, 100 rows a request (the API's ceiling), and
 * neither has a pager any more (owner 2026-10-25: a filter box on every column).
 * The column filters narrow rows on the client, and a filter over one page of a
 * paged list answers a question about that page while looking like an answer
 * about the run. These lists are a run per financial year and a statement per
 * assignment, so "everything" is one request in practice; `MAX_LIST_PAGES` is
 * only a backstop, and the footer's "of {total}" says so if it is ever reached.
 */
const LIST_PER_PAGE = 100;
const MAX_LIST_PAGES = 20;
const SKELETON_ROWS = 5;

/** Every page of a list, in order, as one `Paginated` whose `total` is the server's. */
async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<Paginated<T>>,
): Promise<Paginated<T>> {
  const first = await fetchPage(1);
  const data = [...first.data];
  const lastPage = Math.min(first.meta.totalPages, MAX_LIST_PAGES);
  for (let page = 2; page <= lastPage; page += 1) {
    const next = await fetchPage(page);
    data.push(...next.data);
  }
  return { data, meta: { ...first.meta, page: 1, totalPages: 1 } };
}

/** The ceiling `/target-periods` accepts, so the picker is never short. */
const PERIOD_PER_PAGE = 200;

/* -------------------------------------------------------------------------- *
 * Reading the status, and reading a decimal without touching a float
 * -------------------------------------------------------------------------- */

/**
 * The four states a run can be in, **decided by the FLAGS and in this order**.
 *
 * ⛔ **`isVoid` IS TESTED FIRST AND THAT IS THE WHOLE RULE.** `isApproved` and
 * `isVoid` are not complements: a run is approved and THEN voided, so both are
 * true on that row and the second is what decides. A draft is neither.
 *
 * ⚠️ **`'other'` is a real answer rather than a fallthrough to draft.** The
 * statuses are rows an administrator can add, so a status carrying none of the
 * three flags is expressible — and reading it as a draft would offer Compute,
 * Approve and Delete on a run nobody has classified. It renders as its own name
 * with no controls and no note, which is the honest thing to do about a state
 * this client has never been told about.
 *
 * ⛔ **Nothing here reads `status.name`.** The names are admin-editable labels.
 */
type RunState = 'void' | 'approved' | 'draft' | 'other';

function runState(status: CommissionRunStatus): RunState {
  if (status.isVoid) return 'void';
  if (status.isApproved) return 'approved';
  if (status.isEditable) return 'draft';
  return 'other';
}

function runTone(state: RunState): BadgeTone {
  switch (state) {
    case 'void':
      return 'neutral';
    case 'approved':
      return 'success';
    case 'draft':
      return 'info';
    default:
      return 'warning';
  }
}

/**
 * Whether an exact decimal string is zero, **tested as a STRING**.
 *
 * ⚠️ `'0'`, `'0.00'` and `'0.00000000'` are all zero and all reach this page —
 * `gate_factor` is `DECIMAL(9,8)` and the money columns are `DECIMAL(14,2)`. A
 * `Number()` here would be a float touching a stored figure for the sake of a
 * comparison, which is the habit this repo bans outright rather than bans
 * conditionally: the ban is easy to keep and impossible to keep half of.
 */
function isZeroDecimal(value: string): boolean {
  return /^-?0(\.0+)?$/.test(value.trim());
}

/**
 * The tone for the gate, **read from the FACTOR alone**.
 *
 * ⛔ **`gateMet` IS NOT CONSULTED HERE AND MUST NOT BE.** `src/lib/commissions.ts`
 * states the rule at `gateSummary` and it is the sharpest one on this page: on a
 * sliding scheme `gateMet` answers *did the pool open FULLY*, so it is `false`
 * for every gate between shut and fully open. A tone taken from it paints a pool
 * that really did pay as closed — and the figure beside it contradicts the
 * badge, which is the single most misleading thing this column could do.
 *
 * ⚠️ The WORDS come from `gateSummary`, which is the one place a partial is
 * worded. Nothing here re-derives them.
 */
function gateTone(gateFactor: string): BadgeTone {
  if (/^1(\.0+)?$/.test(gateFactor.trim())) return 'success';
  return isZeroDecimal(gateFactor) ? 'neutral' : 'warning';
}

/**
 * `attainmentTone` answers in the commission module's own vocabulary; `Badge`
 * takes the design system's. Mapped here rather than at three call sites.
 *
 * ⚠️ **`'unknown'` IS NOT `'short'`.** An unreadable or absent percentage is a
 * figure nobody could measure, not a target somebody missed — so it takes the
 * neutral tone rather than the one that reads as a judgement.
 */
const ATTAINMENT_TONES: Record<AttainmentTone, BadgeTone> = {
  met: 'success',
  near: 'warning',
  short: 'danger',
  unknown: 'neutral',
};

/**
 * The lead's name, rendered defensively.
 *
 * `CommissionUserRef.name` is nullable and a staff record can carry an empty
 * one; an empty string in a value slot reads as a missing record rather than as
 * a bad one. The id is the last resort because a statement is what somebody was
 * paid on: it must always name a person, even when the directory could not.
 */
function leadLabel(statement: CommissionStatement): string {
  const name = statement.user.name?.trim();
  if (name) return name;
  const email = statement.user.email.trim();
  if (email) return email;
  return `Staff #${statement.user.id}`;
}

/**
 * The sentence to show for a failure, **ordered by specificity and never by the
 * shape that happened to arrive first**.
 *
 * ⚠️ `formMessage` beats `message`, which on a 422 is only "Validation failed";
 * `detailReasons` renders BESIDE it rather than instead of it, because the
 * sentence says what happened and the reason says why — both true at once, so
 * `message ?? reason` is the silent drop this codebase has written up four
 * times.
 */
function messageOf(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    const base = caught.formMessage ?? caught.message;
    const reasons = caught.detailReasons.join(' ');
    return reasons ? `${base} ${reasons}` : base;
  }
  return caught instanceof Error ? caught.message : fallback;
}

/** `?run=12`, ignoring anything that is not a positive integer. */
function parseId(raw: string | null): number | null {
  if (!raw) return null;
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
}

/* -------------------------------------------------------------------------- *
 * The columns
 * -------------------------------------------------------------------------- */

interface ColumnMeta {
  label: string;
  width: number;
  numeric?: boolean;
}

const RUN_COLUMN_KEYS = ['name', 'period', 'status', 'computed', 'approved'] as const;
type RunColumnKey = (typeof RUN_COLUMN_KEYS)[number];

const RUN_COLUMNS: Record<RunColumnKey, ColumnMeta> = {
  name: { label: 'Run', width: 260 },
  period: { label: 'Period', width: 190 },
  status: { label: 'Status', width: 140 },
  computed: { label: 'Computed', width: 170 },
  approved: { label: 'Approved', width: 170 },
};

const STATEMENT_COLUMN_KEYS = [
  'lead',
  'personal',
  'regional',
  'gate',
  'personalCommission',
  'regionalPool',
  'newBusiness',
  'newBusinessCommission',
  'gross',
  'clawback',
  'net',
] as const;
type StatementColumnKey = (typeof STATEMENT_COLUMN_KEYS)[number];

const STATEMENT_COLUMNS: Record<StatementColumnKey, ColumnMeta> = {
  lead: { label: 'Lead', width: 220 },
  personal: { label: 'Personal attainment', width: 170 },
  regional: { label: 'Regional attainment', width: 170 },
  gate: { label: 'Gate', width: 240 },
  personalCommission: { label: 'Personal commission', width: 170, numeric: true },
  regionalPool: { label: 'Regional pool', width: 180, numeric: true },
  newBusiness: { label: 'New business', width: 170, numeric: true },
  newBusinessCommission: { label: 'New business commission', width: 190, numeric: true },
  gross:{ label: 'Gross', width: 150, numeric: true },
  clawback: { label: 'Clawback', width: 180, numeric: true },
  net: { label: 'Net payable', width: 170, numeric: true },
};

/**
 * The table's floor, summed over the VISIBLE columns only, so hiding columns in
 * the column manager actually narrows the table. ⚠️ It goes through `style`,
 * never a computed `min-w-[…]` class, which Tailwind would never emit.
 */
function minWidthOf<K extends string>(
  keys: readonly string[],
  columns: Record<K, ColumnMeta>,
): number {
  return keys.reduce(
    (total, key) => total + ((columns as Record<string, ColumnMeta | undefined>)[key]?.width ?? 0),
    0,
  );
}

/*
 * The column filters (`useTableFilters`, the house per-column funnel) and the
 * column managers (`useColumnLayout`, hidden ids stored under
 * `columns.commission-runs` / `columns.commission-statements`).
 *
 * Every filter here is a value list over the WHOLE list, because both lists are
 * read whole (see `LIST_PER_PAGE`). Money and percentages are filtered on the
 * FORMATTED text, figure plus currency code — never through `Number()`, and a
 * number range is not offered because the house range control only ever sends
 * server bounds, which these endpoints do not take.
 *
 * ⚠️ The column KEYS are stored against the account (hidden columns, remembered
 * filters). Never rename one.
 */

/** Grouped by UTC day as `YYYY-MM-DD`, which sorts chronologically as text. */
function dayOf(timestamp: string | null): string | null {
  const day = toDateInputValue(timestamp);
  return day === '' ? null : day;
}

/** A money figure as filter text: the grouped decimal string and its code. */
function moneyText(value: string, currency: string): string {
  return `${formatDecimalString(value)} ${currency}`;
}

const RUN_FILTER_COLUMNS: ColumnSpec<CommissionRun>[] = [
  { key: 'name', label: RUN_COLUMNS.name.label, get: (row) => row.name },
  { key: 'period', label: RUN_COLUMNS.period.label, get: (row) => row.period.name },
  // The NAME is what the badge shows, so it is what the list offers. A filter
  // is a view, not behaviour: nothing is decided from it.
  { key: 'status', label: RUN_COLUMNS.status.label, get: (row) => row.status.name },
  // Is empty = never computed / not approved.
  { key: 'computed', label: RUN_COLUMNS.computed.label, get: (row) => dayOf(row.computedAt) },
  { key: 'approved', label: RUN_COLUMNS.approved.label, get: (row) => dayOf(row.approvedAt) },
];

const STATEMENT_FILTER_COLUMNS: ColumnSpec<CommissionStatement>[] = [
  { key: 'lead', label: STATEMENT_COLUMNS.lead.label, get: (row) => leadLabel(row) },
  {
    key: 'personal',
    label: STATEMENT_COLUMNS.personal.label,
    get: (row) => formatPercent(row.personalAttainmentPercent),
  },
  {
    key: 'regional',
    label: STATEMENT_COLUMNS.regional.label,
    // `null` = no regional target, so it is Is empty rather than a value.
    get: (row) =>
      row.regionalAttainmentPercent === null ? null : formatPercent(row.regionalAttainmentPercent),
  },
  { key: 'gate', label: STATEMENT_COLUMNS.gate.label, get: (row) => gateSummary(row) },
  {
    key: 'personalCommission',
    label: STATEMENT_COLUMNS.personalCommission.label,
    numeric: true,
    get: (row) => moneyText(row.personalCommission, row.currency),
  },
  {
    key: 'regionalPool',
    label: STATEMENT_COLUMNS.regionalPool.label,
    numeric: true,
    // The pool that counted, which is the figure the cell leads with.
    get: (row) => moneyText(row.effectiveRegionalPool, row.currency),
  },
  /*
   * ⚠️ `undefined` = a server predating migration 335, so Is empty rather than
   * a zero nobody measured.
   */
  {
    key: 'newBusiness',
    label: STATEMENT_COLUMNS.newBusiness.label,
    numeric: true,
    get: (row) => (row.newBusiness === undefined ? null : moneyText(row.newBusiness, row.currency)),
  },
  {
    key: 'newBusinessCommission',
    label: STATEMENT_COLUMNS.newBusinessCommission.label,
    numeric: true,
    get: (row) =>
      row.newBusinessCommission === undefined
        ? null
        : moneyText(row.newBusinessCommission, row.currency),
  },
  {
    key: 'gross',
    label: STATEMENT_COLUMNS.gross.label,
    numeric: true,
    get: (row) => moneyText(row.grossPayable, row.currency),
  },
  {
    key: 'clawback',
    label: STATEMENT_COLUMNS.clawback.label,
    numeric: true,
    get: (row) => moneyText(row.clawbackAmount, row.currency),
  },
  {
    key: 'net',
    label: STATEMENT_COLUMNS.net.label,
    numeric: true,
    get: (row) => moneyText(row.netPayable, row.currency),
  },
];

/**
 * The identifying column of each table may not be hidden or moved: it holds the
 * only control that opens the row. Module-level, because both hooks memoise on
 * the array's identity.
 */
const RUN_LOCKED_KEYS: readonly string[] = ['name'];
const STATEMENT_LOCKED_KEYS: readonly string[] = ['lead'];

function runLockReason(key: string): string | undefined {
  return key === 'name' ? 'Run holds the control that opens each run, so it cannot be hidden' : undefined;
}

function statementLockReason(key: string): string | undefined {
  return key === 'lead'
    ? 'Lead holds the control that opens each statement, so it cannot be hidden'
    : undefined;
}

/** Frozen-cell props, or nothing for an unfrozen column (see `CyclesPage`). */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const frozen = frozenSideOf(key);
  if (frozen === undefined) return {};
  return { frozen, frozenOffset: offsets[key] ?? 0 };
}

/* -------------------------------------------------------------------------- *
 * Small renderers
 * -------------------------------------------------------------------------- */

/**
 * A money figure with its currency code.
 *
 * ⚠️ **The code is on every figure and is not decoration.** A run can hold
 * statements in several currencies — `currency` is frozen onto each statement
 * from its own scheme — so a bare number is ambiguous on exactly the page where
 * two of them sit in one column.
 */
function Money({
  value,
  currency,
  strong,
}: {
  value: string;
  currency: string;
  strong?: boolean;
}) {
  return (
    <UiMoney amount={value} currency={currency} className={strong ? 'font-semibold text-fg' : undefined} />
  );
}

/**
 * An attainment percentage, or the em dash that means **there is no regional
 * commitment to attain against**.
 *
 * ⚠️ `regionalAttainmentPercent` is nullable and `null` is not zero: a lead with
 * no regional target has not missed one. A `?? '0'` here would render a
 * commitment nobody made as a total failure to meet it.
 */
function Attainment({
  percent,
  gatePercent,
}: {
  percent: string | null;
  gatePercent: string;
}) {
  if (percent === null) {
    return (
      <span className="text-fg-muted" title="This scheme sets no regional target.">
        —
      </span>
    );
  }
  return (
    <Badge tone={ATTAINMENT_TONES[attainmentTone(percent, gatePercent)]}>
      {formatPercent(percent)}
    </Badge>
  );
}

/**
 * The excluded-document marker for ONE figure — see point 4 of the file header.
 *
 * ⛔ **THE TWO COUNTS ARE NEVER ADDED.** `personalExcludedDocuments` and
 * `regionalExcludedDocuments` are taken over **overlapping populations** — a
 * lead's own documents are part of their region's — so one document can be
 * counted in both, and a sum reports a larger gap than the statement has.
 * `EXCLUDED_DOCUMENTS_NOTE` is called once per figure, beside the figure it
 * describes, which is what the module's own header requires.
 *
 * Renders nothing when the count is zero — the healthy case and today's
 * production answer. ⛔ **Never a `0` badge**: one on every row of every run
 * trains the reader to stop seeing it, which is the one outcome that makes the
 * real case invisible.
 */
function ExcludedMarker({ count }: { count: number }) {
  const note = EXCLUDED_DOCUMENTS_NOTE(count);
  if (note === null) return null;
  return (
    <span className="mt-0.5 block">
      <Badge tone="warning" title={note}>
        {count} left out
      </Badge>
    </span>
  );
}

/**
 * Why a control is unavailable, put on a **wrapper span**.
 *
 * ⛔ **A `title` ON A DISABLED `Button` NEVER RENDERS.** `Button` carries
 * `disabled:pointer-events-none`, so the element never receives the hover and
 * the sentence is never shown — a why-disabled note that has never once been
 * read by anybody. The span is outside the button and does receive it.
 *
 * ⚠️ **This carries the PER-RUN reasons only** (already approved, never
 * computed, voided). A permission refusal outranks all of them and is said
 * ONCE, visibly, under the control bar — a reader who may not write anything
 * should be told that in one sentence rather than meeting four tooltips about
 * runs they could not act on either way.
 */
function ControlGuard({ reason, children }: { reason: string | null; children: ReactNode }) {
  if (reason === null) return <>{children}</>;
  return (
    <span title={reason} className="inline-flex">
      {children}
    </span>
  );
}

/**
 * A fact the reader cannot otherwise see, rendered as one muted line.
 *
 * ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT DELETE IT.** Every sentence routed
 * through here names a state nothing else on screen shows: that a draft's
 * figures move with the account leads, that an approved run's do not, that a
 * credited invoice still counts at full value. The no-helper-text prohibition
 * covers advice attached to a control; this is a statement about the data.
 */
function DataNote({ children }: { children: ReactNode }) {
  return <p className="m-0 text-xs leading-relaxed text-fg-muted">{children}</p>;
}

/**
 * One of the module's `{short, detail}` notes, rendered as its short form with
 * the whole fact on a `title`.
 *
 * ⚠️ **The `detail` is not helper text hidden in a tooltip — it is the SAME
 * FACT at length**, which is why the short form has to stand alone. A reader on
 * a touch device never sees a `title`, so nothing they NEED may live only
 * there; here the short form carries the finding and the detail carries the
 * reasoning.
 */
function NoteLine({ note }: { note: { short: string; detail: string } }) {
  return (
    <p className="m-0 text-xs leading-relaxed text-fg-muted" title={note.detail}>
      {note.short}
    </p>
  );
}

/** One runs-table cell, keyed by column so hidden and reordered columns follow. */
function RunCell({
  column,
  entry,
  selected,
  onSelect,
}: {
  column: string;
  entry: CommissionRun;
  selected: boolean;
  onSelect: (id: number) => void;
}) {
  switch (column) {
    case 'name':
      return (
        /*
         * A real `<button>` rather than a click handler on the `<tr>`: a row is
         * not focusable and does not activate on Enter, so a handler there is a
         * control only a mouse can reach.
         */
        <button
          type="button"
          onClick={() => onSelect(entry.id)}
          className="text-left text-sm font-medium text-fg [overflow-wrap:anywhere] hover:underline"
          aria-current={selected ? 'true' : undefined}
        >
          {entry.name}
        </button>
      );
    case 'period':
      return (
        /* `period` is resolved server-side and is never null, so there is no id
           fallback to render and no guessed name to invent. */
        <span
          className="text-sm text-fg-secondary"
          title={`${formatDateOnly(entry.period.startDate)} – ${formatDateOnly(entry.period.endDate)}`}
        >
          {entry.period.name}
        </span>
      );
    case 'status':
      // The NAME is rendered and the TONE comes from the FLAGS — see `runState`.
      return <Badge tone={runTone(runState(entry.status))}>{entry.status.name}</Badge>;
    case 'computed':
      return (
        <span className="whitespace-nowrap tabular text-xs">
          {entry.computedAt === null ? '—' : formatDateTime(entry.computedAt)}
        </span>
      );
    case 'approved':
      /*
       * ⛔ **`approvedAt` DECIDES, NEVER THE PRESENCE OF THE PERSON.** The actor
       * column is `SET NULL`, so a departed colleague must not make an approved
       * run read as unapproved.
       */
      return (
        <span className="whitespace-nowrap tabular text-xs">
          {entry.approvedAt === null ? '—' : formatDateTime(entry.approvedAt)}
        </span>
      );
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- *
 * The page
 * -------------------------------------------------------------------------- */

export function CommissionRunsPage() {
  const { ability } = useAuth();

  /*
   * ⛔ **GATED ON `ability`, NEVER ON `user.role`.** The commissions module is
   * enforced server-side on the permission SETS — there is no `requireRole` on
   * that router — so a role test here would answer a question the server never
   * asked, in both directions.
   *
   * ⚠️ **`approve` IS GATED ON `update` DELIBERATELY.** Migration 205 grants
   * all five actions to the same two administrator sets, so the two cannot
   * diverge today; and where a gate must guess, this repo's standing rule is to
   * prefer over-offering to over-restricting, because the refusal is handled
   * below and a wrongly-hidden control is a false statement with nobody
   * watching. ⚠️ If `canApprove` ever takes this module, move the two approve
   * gates to it rather than widening this comment.
   */
  const canRead = ability.canRead(COMMISSIONS_MODULE);
  const canCreate = ability.canCreate(COMMISSIONS_MODULE);
  const canUpdate = ability.canUpdate(COMMISSIONS_MODULE);
  const canDelete = ability.canDelete(COMMISSIONS_MODULE);
  const canApprove = ability.canApprove('commissions');

  const readDenial = denialReason(ability, 'read', COMMISSIONS_MODULE);
  const writeDenial = denialReason(ability, 'update', COMMISSIONS_MODULE);

  // No argument: the hook's first parameter is a DEFAULT INIT, not options, so
  // `useSearchParams({ replace: true })` injects a literal `replace=true` into
  // the query string and then into everything parsed out of it.
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();

  const selectedRunId = useMemo(
    () => parseId(new URLSearchParams(queryString).get('run')),
    [queryString],
  );
  const selectedStatementId = useMemo(
    () => parseId(new URLSearchParams(queryString).get('statement')),
    [queryString],
  );

  const [runs, setRuns] = useState<Paginated<CommissionRun> | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [runsLoading, setRunsLoading] = useState(true);

  const [run, setRun] = useState<CommissionRun | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [runLoading, setRunLoading] = useState(false);

  const [statements, setStatements] = useState<Paginated<CommissionStatement> | null>(null);
  const [statementsError, setStatementsError] = useState<string | null>(null);
  const [statementsLoading, setStatementsLoading] = useState(false);

  /*
   * The outcome of the last compute, kept for its `skipped` list alone.
   *
   * ⚠️ **Cleared whenever the selected run changes**, because a list of
   * assignments that could not be computed is about ONE run and reads as being
   * about whatever is on screen.
   */
  const [compute, setCompute] = useState<CommissionRunComputeResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'compute' | 'approve' | 'void' | 'delete' | null>(null);

  const [confirming, setConfirming] = useState<'compute' | 'approve' | 'void' | 'delete' | null>(
    null,
  );
  const [confirmError, setConfirmError] = useState<string | null>(null);
  /*
   * Holds the confirm button down when pressing it again cannot do anything
   * different — the delete 409 that says to void instead will repeat
   * identically until somebody voids the run. Only ever set beside the error
   * that says why: a bare disabled button makes a refusal, a permission denial
   * and an ordinary state look the same.
   */
  const [confirmFinal, setConfirmFinal] = useState(false);

  const [creating, setCreating] = useState(false);

  /* ── The URL is the store ─────────────────────────────────────────────── */

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(queryString);
      if (value === null) params.delete(key);
      else params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  const selectRun = useCallback(
    (id: number | null) => {
      const params = new URLSearchParams(queryString);
      if (id === null) params.delete('run');
      else params.set('run', String(id));
      // A different run's statements are a different set, so the open one
      // cannot survive the move — and a stale `?statement=` would render a
      // panel for a row that is no longer in the table.
      params.delete('statement');
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  /* ── The runs list ────────────────────────────────────────────────────── */

  const loadRuns = useCallback(
    (signal?: AbortSignal) => {
      setRunsLoading(true);
      return fetchAllPages((page) => {
        const params: CommissionRunListParams = { page, perPage: LIST_PER_PAGE };
        return api.listCommissionRuns(params, signal);
      })
        .then((data) => {
          setRuns(data);
          setRunsError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setRunsError(messageOf(caught, 'Failed to load commission runs'));
        })
        .finally(() => {
          if (!signal?.aborted) setRunsLoading(false);
        });
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    // The `setLoading(true)` inside the loader is the effect synchronising the
    // page with a request it is starting — which is what an effect is for. The
    // rule cannot see through the callback to say so.
    // oxlint-disable-next-line react/set-state-in-effect
    void loadRuns(controller.signal);
    return () => controller.abort();
  }, [loadRuns]);

  /* ── The selected run ─────────────────────────────────────────────────── */

  const loadRun = useCallback(
    (signal?: AbortSignal) => {
      if (selectedRunId === null) {
        setRun(null);
        setRunError(null);
        return Promise.resolve();
      }
      setRunLoading(true);
      return api
        .getCommissionRun(selectedRunId, signal)
        .then((answer) => {
          setRun(answer.data);
          setRunError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setRun(null);
          setRunError(messageOf(caught, 'Failed to load that run'));
        })
        .finally(() => {
          if (!signal?.aborted) setRunLoading(false);
        });
    },
    [selectedRunId],
  );

  useEffect(() => {
    const controller = new AbortController();
    // oxlint-disable-next-line react/set-state-in-effect
    void loadRun(controller.signal);
    return () => controller.abort();
  }, [loadRun]);

  /*
   * A compute outcome belongs to the run it was run against, so it resets when
   * the selection moves.
   */
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setCompute(null);
    // oxlint-disable-next-line react/set-state-in-effect
    setActionError(null);
  }, [selectedRunId]);

  /* ── The statements of the selected run ───────────────────────────────── */

  const loadStatements = useCallback(
    (signal?: AbortSignal) => {
      if (selectedRunId === null) {
        setStatements(null);
        setStatementsError(null);
        return Promise.resolve();
      }
      setStatementsLoading(true);
      const runId = selectedRunId;
      return fetchAllPages((page) => {
        const params: CommissionStatementListParams = { runId, page, perPage: LIST_PER_PAGE };
        return api.listCommissionStatements(params, signal);
      })
        .then((data) => {
          setStatements(data);
          setStatementsError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setStatementsError(messageOf(caught, 'Failed to load the statements'));
        })
        .finally(() => {
          if (!signal?.aborted) setStatementsLoading(false);
        });
    },
    [selectedRunId],
  );

  useEffect(() => {
    const controller = new AbortController();
    // oxlint-disable-next-line react/set-state-in-effect
    void loadStatements(controller.signal);
    return () => controller.abort();
  }, [loadStatements]);

  /* ── The write paths ──────────────────────────────────────────────────── */

  /**
   * Everything a landed write moves, reloaded together.
   *
   * ⚠️ A compute rewrites the statements AND the run's `computedAt`; an
   * approval moves the status on the run AND in the list. Reloading one of the
   * three leaves the other two stating yesterday's answer with nothing failing,
   * which on this page means a frozen run still offering Compute.
   */
  const reloadEverything = useCallback(async () => {
    await Promise.all([loadRuns(), loadRun(), loadStatements()]);
  }, [loadRuns, loadRun, loadStatements]);

  const runCompute = useCallback(async () => {
    if (selectedRunId === null) return;
    setBusy('compute');
    setActionError(null);
    try {
      const answer = await api.computeCommissionRun(selectedRunId);
      setCompute(answer);
      setConfirming(null);
      setConfirmError(null);
      await reloadEverything();
    } catch (caught: unknown) {
      const message = messageOf(caught, 'The run could not be computed');
      // Inside the dialog when one is open, beside the controls when it is not:
      // a refusal must land where the reader pressed.
      if (confirming === 'compute') setConfirmError(message);
      else setActionError(message);
    } finally {
      setBusy(null);
    }
  }, [selectedRunId, reloadEverything, confirming]);

  const runApprove = useCallback(async () => {
    if (selectedRunId === null) return;
    setBusy('approve');
    setConfirmError(null);
    try {
      await api.approveCommissionRun(selectedRunId);
      setConfirming(null);
      await reloadEverything();
    } catch (caught: unknown) {
      setConfirmError(messageOf(caught, 'The run could not be approved'));
    } finally {
      setBusy(null);
    }
  }, [selectedRunId, reloadEverything]);

  const runVoid = useCallback(async () => {
    if (selectedRunId === null) return;
    setBusy('void');
    setConfirmError(null);
    try {
      await api.voidCommissionRun(selectedRunId);
      setConfirming(null);
      await reloadEverything();
    } catch (caught: unknown) {
      setConfirmError(messageOf(caught, 'The run could not be voided'));
    } finally {
      setBusy(null);
    }
  }, [selectedRunId, reloadEverything]);

  const runDelete = useCallback(async () => {
    if (selectedRunId === null) return;
    setBusy('delete');
    setConfirmError(null);
    try {
      await api.deleteCommissionRun(selectedRunId);
      setConfirming(null);
      selectRun(null);
      await loadRuns();
    } catch (caught: unknown) {
      /*
       * ⚠️ **The 409 is the server telling the reader to VOID instead, and its
       * own sentence is the whole of the useful part** — it names the state the
       * run is in and the verb that applies to it. Rendered verbatim, with the
       * confirm button held down, because pressing Delete again cannot produce
       * a different answer until somebody voids the run.
       */
      setConfirmError(messageOf(caught, 'The run could not be deleted'));
      setConfirmFinal(caught instanceof ApiError && caught.status === 409);
    } finally {
      setBusy(null);
    }
  }, [selectedRunId, selectRun, loadRuns]);

  const openConfirm = useCallback((which: 'compute' | 'approve' | 'void' | 'delete') => {
    setConfirming(which);
    setConfirmError(null);
    setConfirmFinal(false);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirming(null);
    setConfirmError(null);
    setConfirmFinal(false);
  }, []);

  /* ── Derived state ────────────────────────────────────────────────────── */

  // Memoised: `useTableFilters` derives its rows and options from this array.
  const runRows = useMemo(() => runs?.data ?? [], [runs]);
  const runsTotal = runs?.meta.total ?? 0;
  const runsInitialLoad = runsLoading && runs === null;

  const runFilters = useTableFilters(runRows, RUN_FILTER_COLUMNS, { memoryId: 'runs' });
  const runVisibleRows = runFilters.visibleRows;
  const runsHiddenByFilters = runRows.length - runVisibleRows.length;
  const runLayout = useColumnLayout('commission-runs', RUN_COLUMN_KEYS, RUN_LOCKED_KEYS);
  const runHeadRowRef = useRef<HTMLTableRowElement | null>(null);
  const runFrozenOffsets = useFrozenOffsets(
    runHeadRowRef,
    runLayout.visibleKeys,
    runLayout.frozenSideOf,
  );
  const runMinWidth = useMemo(
    () => minWidthOf(runLayout.visibleKeys, RUN_COLUMNS),
    [runLayout.visibleKeys],
  );

  /*
   * Memoised on `statements` because three memos below take it as a dependency;
   * `?? []` alone is a fresh array every render, which keys them on identity and
   * recomputes for ever.
   */
  const statementRows = useMemo(() => statements?.data ?? [], [statements]);
  const statementsTotal = statements?.meta.total ?? 0;
  const statementsInitialLoad = statementsLoading && statements === null;

  const state = run ? runState(run.status) : null;
  const computed = run?.computedAt ?? null;

  const selectedStatement = useMemo(
    () => statementRows.find((entry) => entry.id === selectedStatementId) ?? null,
    [statementRows, selectedStatementId],
  );

  /**
   * The currencies on the loaded page.
   *
   * ⚠️ Read **only to word the absence of a total**, never to compute one — see
   * the file header. A run holding two codes is an ordinary state, and saying
   * so is the honest alternative to a number that would be wrong by
   * construction.
   */
  const currencies = useMemo(
    () => Array.from(new Set(statementRows.map((entry) => entry.currency))).sort(),
    [statementRows],
  );

  /**
   * Every FIGURE on this page that lost a document to a missing rate.
   *
   * Gathered so the panel can say it in words above the table rather than
   * leaving it to a badge a reader has to hover to find — these counts are the
   * only thing on this screen that can say a commission figure is SHORT.
   *
   * ⛔ **ONE ENTRY PER FIGURE, NEVER ONE PER STATEMENT WITH THE COUNTS ADDED.**
   * The personal and regional counts are taken over overlapping populations —
   * a lead's own documents are part of their region's — so a document can be in
   * both, and a summed figure reports a larger gap than the statement has.
   */
  const excluded = useMemo(
    () =>
      statementRows.flatMap((entry) =>
        (
          [
            { scope: 'Personal revenue', count: entry.personalExcludedDocuments },
            { scope: 'Regional revenue', count: entry.regionalExcludedDocuments },
          ] as const
        ).flatMap(({ scope, count }) => {
          const note = EXCLUDED_DOCUMENTS_NOTE(count);
          return note === null ? [] : [{ statement: entry, scope, note }];
        }),
      ),
    [statementRows],
  );

  /* ── What each control may do ─────────────────────────────────────────── */

  const computeReason = !run
    ? 'Choose a run first.'
    : state === 'approved'
      ? 'An approved run cannot be recomputed. Void it to replace it.'
      : state === 'void'
        ? 'This run has been voided.'
        : !run.status.isEditable
          ? 'This run is not editable.'
          : null;

  const approveReason = !run
    ? 'Choose a run first.'
    : state === 'approved'
      ? 'This run is already approved.'
      : state === 'void'
        ? 'This run has been voided.'
        : computed === null
          ? 'Compute the run before approving it — there are no figures to freeze yet.'
          : !run.status.isEditable
            ? 'This run is not editable.'
            : null;

  const voidReason = !run
    ? 'Choose a run first.'
    : state === 'void'
      ? 'This run is already voided.'
      : null;

  const deleteReason = !run
    ? 'Choose a run first.'
    : !run.status.isEditable
      ? 'Only an editable run can be deleted. Void this one instead — the statements stay as a record of what was paid.'
      : null;

  const periodName = run?.period.name ?? null;

  /* The three stacked panels are movable; the header stays fixed. */
  const layout = usePageLayout('commission-runs', [
    { id: 'runs', label: 'Runs' },
    { id: 'run', label: 'Selected run' },
    { id: 'statement', label: 'Statement' },
  ]);

  return (
    <>
      <PageHeader
        eyebrow="Commissions"
        title="Commission runs"
        /*
         * ⚠️ **`COMMISSION_VISIBILITY_NOTE` IS NOT HELPER TEXT.** It states who
         * can see this screen at all, which is a fact about the SYSTEM that no
         * control on the page conveys — and on the one surface in this app that
         * discloses pay, a reader who does not know it is a reader who might
         * screen-share it.
         */
        description={
          <span title={COMMISSION_VISIBILITY_NOTE.detail}>{COMMISSION_VISIBILITY_NOTE.short}</span>
        }
        actions={
          <>
            {canCreate ? (
              /*
               * `secondary`, not `primary`: the run panel's Approve is the one
               * primary action on this screen, and both can be visible at once.
               */
              <Button variant="secondary" leadingIcon={<PlusIcon />} onClick={() => setCreating(true)}>
                New run
              </Button>
            ) : null}
            <LayoutToolbar layout={layout} />
          </>
        }
      />

      <LayoutGrid
        layout={layout}
        render={{
          /* ── The runs ── */
          runs: () => (
      <Panel>
        <PanelHeader title="Runs" description="One per financial year." />

        {canRead && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-b border-border px-4 py-2">
            {/* Cards have no header row to hang a funnel on, so below `md` the
                same filters open in a sheet. */}
            <div className="mr-auto md:hidden">
              <FilterSheet
                keys={runLayout.orderedKeys}
                headerFor={runFilters.headerFor}
                activeCount={runFilters.activeCount}
                clearAll={runFilters.clearAll}
              />
            </div>
            <ColumnManager
              layout={runLayout}
              labelOf={(key) => RUN_COLUMNS[key as RunColumnKey]?.label ?? key}
              lockedKeys={RUN_LOCKED_KEYS}
              lockReasonOf={runLockReason}
            />
          </div>
        )}

        {runsError && (
          <Notice tone="danger" role="alert" className="m-3" title={<>Couldn&rsquo;t load commission runs.</>}>
            <span className="[overflow-wrap:anywhere]">{runsError}</span>
          </Notice>
        )}

        <div
          aria-busy={runsLoading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            runsLoading && runs && 'opacity-60',
          )}
        >
          {/* Below `md`: a stacked card per run. Five columns will not fit
              320px, and a run that exists only above a breakpoint is
              indistinguishable from one that was removed. */}
          <div className="md:hidden">
            {runsInitialLoad ? (
              <ul className="m-0 list-none p-0">
                {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                  <li key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="mt-2 h-2.5 w-24" />
                  </li>
                ))}
              </ul>
            ) : runRows.length === 0 ? (
              <RunsEmptyState hasError={runsError !== null} denial={canRead ? null : readDenial} />
            ) : runVisibleRows.length === 0 ? (
              <FilteredEmptyState noun="runs" onClear={runFilters.clearAll} />
            ) : (
              <ul className="m-0 list-none p-0">
                {/* The card shows the columns the table shows: a column hidden
                    in the column manager is hidden here too. */}
                {runVisibleRows.map((entry) => (
                  <li
                    key={entry.id}
                    className={cx(
                      'border-b border-border px-4 py-3 last:border-b-0',
                      entry.id === selectedRunId && 'bg-surface-sunken',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => selectRun(entry.id)}
                        className="min-w-0 text-left"
                      >
                        <span className="block text-sm font-medium text-fg [overflow-wrap:anywhere]">
                          {entry.name}
                        </span>
                        {runLayout.isVisible('period') && (
                          <span className="mt-0.5 block text-xs text-fg-muted">
                            {entry.period.name}
                          </span>
                        )}
                      </button>
                      {runLayout.isVisible('status') && (
                        <Badge tone={runTone(runState(entry.status))}>{entry.status.name}</Badge>
                      )}
                    </div>
                    {runLayout.isVisible('computed') && (
                      <p className="mt-1.5 m-0 text-xs text-fg-muted tabular">
                        {entry.computedAt === null
                          ? 'Never computed'
                          : `Computed ${formatDateTime(entry.computedAt)}`}
                      </p>
                    )}
                    {runLayout.isVisible('approved') && entry.approvedAt !== null && (
                      <p className="mt-0.5 m-0 text-xs text-fg-muted tabular">
                        Approved {formatDateTime(entry.approvedAt)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* From `md`: the table. ONE mount, with only the `<TBody>`
              swapping — two branches would mean two header rows and, with them,
              two different measured layouts. */}
          <div className="hidden md:block">
            {!runsInitialLoad && runRows.length === 0 ? (
              <RunsEmptyState hasError={runsError !== null} denial={canRead ? null : readDenial} />
            ) : (
              <>
              <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                {/* ⚠️ **The floor goes through `style`, not a class.** Tailwind
                    v4 scans source text, so a computed `min-w-[…]` emits no rule
                    at all — the class lands in the DOM and does nothing. */}
                <Table style={{ minWidth: runMinWidth }}>
                  <THead>
                    {/* The measured row for frozen-column offsets. The header
                        stays mounted when the filters empty the table, so the
                        funnel that did it is still there to undo. */}
                    <TR ref={runHeadRowRef}>
                      {runLayout.visibleKeys.map((key) => (
                        <FilterableTH
                          key={key}
                          {...runFilters.headerFor(key)}
                          {...frozenPropsFor(key, runLayout.frozenSideOf, runFrozenOffsets)}
                        />
                      ))}
                    </TR>
                  </THead>
                  {runsInitialLoad ? (
                    <TBody>
                      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                        <TR key={index}>
                          {runLayout.visibleKeys.map((key) => (
                            <TD
                              key={key}
                              {...frozenPropsFor(key, runLayout.frozenSideOf, runFrozenOffsets)}
                            >
                              <Skeleton className="h-3 w-24" />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  ) : (
                    <TBody>
                      {runVisibleRows.map((entry) => (
                        <TR
                          key={entry.id}
                          interactive
                          className={cx(entry.id === selectedRunId && 'bg-surface-sunken')}
                        >
                          {runLayout.visibleKeys.map((key) => (
                            <TD
                              key={key}
                              {...frozenPropsFor(key, runLayout.frozenSideOf, runFrozenOffsets)}
                            >
                              <RunCell
                                column={key}
                                entry={entry}
                                selected={entry.id === selectedRunId}
                                onSelect={selectRun}
                              />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>
              {!runsInitialLoad && runVisibleRows.length === 0 && (
                <FilteredEmptyState noun="runs" onClear={runFilters.clearAll} />
              )}
              </>
            )}
          </div>
        </div>

        {runs && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {runsTotal === 0
                ? 'No runs to show'
                : `Showing ${runRows.length} of ${runsTotal}`}
              {runsHiddenByFilters > 0 && (
                <span className="text-fg-subtle"> · {runsHiddenByFilters} hidden by column filters</span>
              )}
            </p>
          </PanelFooter>
        )}
      </Panel>
          ),
          /* ── The selected run ── */
          run: () => (
      <div className="min-w-0">
        {selectedRunId === null ? (
          <Panel>
            <EmptyState
              title="Choose a run"
              description="Pick a run above to see its statements."
            />
          </Panel>
        ) : (
          <Panel>
            <PanelHeader
              title={run ? run.name : 'Run'}
              description={
                run ? (
                  <span className="tabular">
                    {run.period.name}
                    <span className="mx-1.5 text-fg-subtle">·</span>
                    {run.statementCount === 1 ? '1 statement' : `${run.statementCount} statements`}
                    {computed !== null && (
                      <>
                        <span className="mx-1.5 text-fg-subtle">·</span>
                        Computed {formatDateTime(computed)}
                      </>
                    )}
                  </span>
                ) : undefined
              }
              /*
               * ⚠️ **ONLY THE BADGE GOES IN `actions`.** `PanelHeader`'s slot is
               * `shrink-0` and does not wrap, so a bank of controls in it runs
               * past the panel at 320px — the failure this repo already records
               * against putting a filter bank there. The four buttons get a row
               * of their own below, which CAN wrap.
               */
              actions={
                run ? (
                  <Badge tone={runTone(runState(run.status))} dot>
                    {run.status.name}
                  </Badge>
                ) : undefined
              }
            />

            {run && (canUpdate || canApprove || canDelete) && (
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
                {canUpdate && (
                  <>
                    <ControlGuard reason={computeReason}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={computeReason !== null || busy !== null}
                        loading={busy === 'compute'}
                        onClick={() => {
                          /*
                           * ⚠️ **A FIRST compute needs no confirmation and a
                           * RECOMPUTE does.** The figures freeze at compute, so
                           * recomputing legitimately MOVES numbers somebody may
                           * be reviewing — the one thing that may, and the one
                           * thing worth naming before it happens.
                           */
                          if (computed === null) void runCompute();
                          else openConfirm('compute');
                        }}
                      >
                        {computed === null ? 'Compute' : 'Recompute'}
                      </Button>
                    </ControlGuard>
                  </>
                )}
                {/*
                 * 2026-10-02 (owner): approving a run is `commissions.approve`,
                 * which the server already checks. *SUPERSEDED: gated on
                 * `canUpdate` with compute and void.*
                 */}
                {canApprove && (
                  <ControlGuard reason={approveReason}>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={approveReason !== null || busy !== null}
                      onClick={() => openConfirm('approve')}
                    >
                      Approve
                    </Button>
                  </ControlGuard>
                )}
                {canUpdate && (
                  <>
                    <ControlGuard reason={voidReason}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={voidReason !== null || busy !== null}
                        onClick={() => openConfirm('void')}
                      >
                        Void
                      </Button>
                    </ControlGuard>
                  </>
                )}
                {canDelete && (
                  <ControlGuard reason={deleteReason}>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={deleteReason !== null || busy !== null}
                      onClick={() => openConfirm('delete')}
                    >
                      Delete
                    </Button>
                  </ControlGuard>
                )}
              </div>
            )}

            {runError && (
              <Notice tone="danger" role="alert" className="m-3" title={<>Couldn&rsquo;t load that run.</>}>
                <span className="[overflow-wrap:anywhere]">{runError}</span>
              </Notice>
            )}

            {actionError && (
              <Notice tone="danger" role="alert" className="m-3" title={<>That didn&rsquo;t work.</>}>
                <span className="[overflow-wrap:anywhere]">{actionError}</span>
              </Notice>
            )}

            {runLoading && run === null && !runError ? (
              <PanelBody>
                <Skeleton className="h-3 w-56" />
                <Skeleton className="mt-2 h-3 w-40" />
              </PanelBody>
            ) : run === null ? null : (
              <>
                <PanelBody className="flex flex-col gap-2 border-b border-border">
                  {/*
                    * ⚠️ **ONE of these two, decided by the FLAGS.**
                    *
                    * A run whose status is still editable can move — either it
                    * has never been computed, or a recompute would re-resolve
                    * the account leads and rewrite every figure. An approved run
                    * cannot. ⛔ A status carrying neither flag gets NEITHER
                    * sentence rather than a guess: both are claims about
                    * whether the numbers can still change, and a wrong one is
                    * believed.
                    */}
                  {state === 'draft' && <NoteLine note={LIVE_ATTRIBUTION_NOTE} />}
                  {state === 'approved' && <NoteLine note={FROZEN_STATEMENT_NOTE} />}
                  {state === 'void' && computed !== null && (
                    <NoteLine note={FROZEN_STATEMENT_NOTE} />
                  )}

                  {/* One sentence, once, for a reader who may not write
                      anything — rather than the same refusal on four
                      tooltips. */}
                  {!canUpdate && writeDenial && <DataNote>{writeDenial}</DataNote>}

                  {run.notes && (
                    <p className="m-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
                      {run.notes}
                    </p>
                  )}
                </PanelBody>

                {/*
                  * ⛔ **THE SKIPPED LIST IS NEUTRAL, NOT A DANGER BLOCK.** An
                  * assignment that could not be computed is an ORDINARY
                  * outcome — the run did exactly what it was asked to and is
                  * saying what it could not cover. Wording it as a failure puts
                  * a red banner on the normal case, and the real point is the
                  * opposite one: these people have NO statement, which is a
                  * different fact from a statement of 0.00 and is the only
                  * place it is visible.
                  */}
                {compute && compute.skipped.length > 0 && (
                  <PanelBody className="border-b border-border">
                    <p className="m-0 text-xs font-semibold text-fg-secondary">
                      Not computed ({compute.skipped.length})
                    </p>
                    <p className="mt-0.5 m-0 text-xs text-fg-muted">
                      These assignments produced no statement. They are not on the table below.
                    </p>
                    <ul className="mt-2 m-0 flex list-none flex-col gap-1 p-0">
                      {compute.skipped.map((entry) => (
                        <li
                          key={entry.assignmentId}
                          className="rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-xs text-fg-secondary [overflow-wrap:anywhere]"
                        >
                          <span className="font-medium text-fg">{entry.userName}</span>
                          {' — '}
                          {/* The server's own sentence, verbatim: it names the
                              condition, which is the only actionable part. */}
                          {entry.reason}
                        </li>
                      ))}
                    </ul>
                  </PanelBody>
                )}

                {/*
                  Leads whose clawback remainder this run cannot land, because
                  they hold no assignment in its period — owner decision
                  2026-09-24. Renders nothing when there are none. Re-read when
                  the run's own state moves, so a compute or approval that
                  changed the answer is not left stating yesterday's.
                */}
                {run && (
                  <StrandedRemaindersPanel
                    runId={run.id}
                    reloadKey={`${run.computedAt ?? ''}|${run.approvedAt ?? ''}|${run.voidedAt ?? ''}`}
                  />
                )}

                <StatementsSection
                  rows={statementRows}
                  total={statementsTotal}
                  initialLoad={statementsInitialLoad}
                  loading={statementsLoading}
                  error={statementsError}
                  currencies={currencies}
                  excluded={excluded}
                  selectedId={selectedStatementId}
                  onSelect={(id) => setParam('statement', id === null ? null : String(id))}
                  computed={computed !== null}
                />
              </>
            )}
          </Panel>
        )}
      </div>
          ),
          /* ── The one open statement ── */
          statement: () =>
            selectedStatement ? <StatementPanel statement={selectedStatement} /> : null,
        }}
      />


      {/* ── Confirmations ──────────────────────────────────────────────── */}

      <ConfirmDialog
        open={confirming === 'compute'}
        onClose={closeConfirm}
        onConfirm={() => void runCompute()}
        title="Recompute this run?"
        tone="primary"
        confirmLabel="Recompute"
        loading={busy === 'compute'}
        {...(confirmError ? { error: confirmError } : {})}
        description={
          <>
            <p className="m-0">
              Every statement on <strong>{run?.name}</strong> is replaced. The figures are
              recalculated from the revenue as it stands today, so they can differ from the ones on
              screen.
            </p>
            {/* The one thing a reader cannot see: a recompute re-resolves the
                account leads, so a company that moved between leads moves its
                revenue with it. */}
            <p className="mt-2 m-0">{LIVE_ATTRIBUTION_NOTE.detail}</p>
          </>
        }
      />

      <ConfirmDialog
        open={confirming === 'approve'}
        onClose={closeConfirm}
        onConfirm={() => void runApprove()}
        title="Approve this run?"
        tone="primary"
        confirmLabel="Approve"
        loading={busy === 'approve'}
        {...(confirmError ? { error: confirmError } : {})}
        description={
          <>
            <p className="m-0">
              Approving <strong>{run?.name}</strong> freezes what each lead is owed for{' '}
              <strong>{periodName ?? 'this period'}</strong>. The run can no longer be recomputed,
              and no second run can be approved for the same period until this one is voided.
            </p>
            {/*
              * ⚠️ **THE CLAWBACK SENTENCE IS THE ONE NOBODY EXPECTS, AND IT IS
              * NOT HELPER TEXT.** The natural assumption is that a credit
              * raised against this year reopens it. It does not — it lands in
              * the year it was raised, as a clawback against that year's
              * payout — and nothing else on this screen says so.
              */}
            <p className="mt-2 m-0">
              A credit raised after this is approved does not reopen this year: it is taken back
              from the year it is raised in.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={confirming === 'void'}
        onClose={closeConfirm}
        onConfirm={() => void runVoid()}
        title="Void this run?"
        confirmLabel="Void run"
        loading={busy === 'void'}
        {...(confirmError ? { error: confirmError } : {})}
        description={
          <>
            <p className="m-0">
              <strong>{run?.name}</strong> stops counting as the approved run for{' '}
              <strong>{periodName ?? 'this period'}</strong>, so another run can be approved in its
              place.
            </p>
            {/* Voiding is not deleting, and saying which is the point: the
                statements remain as the record of what was computed. */}
            <p className="mt-2 m-0">
              Its statements are kept. Nothing already paid against them is reversed here.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={confirming === 'delete'}
        onClose={closeConfirm}
        onConfirm={() => void runDelete()}
        title="Delete this run?"
        confirmLabel="Delete run"
        loading={busy === 'delete'}
        confirmDisabled={confirmFinal}
        {...(confirmError ? { error: confirmError } : {})}
        description={
          <p className="m-0">
            <strong>{run?.name}</strong> and every statement computed for it are removed. This
            cannot be undone. An approved run cannot be deleted — void it instead.
          </p>
        }
      />

      {creating && (
        <CreateRunModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            selectRun(id);
            void loadRuns();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * The statements
 * -------------------------------------------------------------------------- */

/** One statements-table cell, keyed by column so hidden and reordered columns follow. */
function StatementCell({
  column,
  statement,
  selected,
  onSelect,
}: {
  column: string;
  statement: CommissionStatement;
  selected: boolean;
  onSelect: (id: number | null) => void;
}) {
  switch (column) {
    case 'lead':
      return (
        <button
          type="button"
          onClick={() => onSelect(selected ? null : statement.id)}
          className="text-left text-sm font-medium text-fg [overflow-wrap:anywhere] hover:underline"
          aria-current={selected ? 'true' : undefined}
        >
          {leadLabel(statement)}
        </button>
      );
    case 'personal':
      return (
        <Attainment
          percent={statement.personalAttainmentPercent}
          gatePercent={statement.personalGatePercent}
        />
      );
    case 'regional':
      return (
        <Attainment
          percent={statement.regionalAttainmentPercent}
          gatePercent={statement.regionalGatePercent}
        />
      );
    case 'gate':
      /*
       * ⛔ **THE WORDS COME FROM `gateSummary` AND THE TONE FROM THE FACTOR —
       * `gateMet` is consulted by neither.** On a sliding scheme it is `false`
       * for every gate between shut and fully open, so a label taken from it
       * reads "not met" beside a pool that really did pay.
       */
      return <Badge tone={gateTone(statement.gateFactor)}>{gateSummary(statement)}</Badge>;
    case 'personalCommission':
      return (
        <>
          <Money value={statement.personalCommission} currency={statement.currency} />
          {/*
            * ⛔ **ONE COUNT PER FIGURE.** This is the documents missing from the
            * PERSONAL revenue; the regional count sits on the regional figure
            * and the two are never added — their populations overlap.
            */}
          <ExcludedMarker count={statement.personalExcludedDocuments} />
        </>
      );
    case 'regionalPool':
      return (
        <>
          {/*
            * ⚠️ **THE FIGURE IS THE POOL THAT ACTUALLY COUNTED**, which is what
            * feeds Gross. Where the gate held some of it back, the full pool is
            * named beneath — rendering the full one alone would report money
            * nobody was paid.
            */}
          <Money value={statement.effectiveRegionalPool} currency={statement.currency} />
          {statement.effectiveRegionalPool !== statement.regionalPool && (
            <span className="mt-0.5 block text-xs text-fg-muted tabular">
              of {formatDecimalString(statement.regionalPool)} before the gate
            </span>
          )}
          <ExcludedMarker count={statement.regionalExcludedDocuments} />
        </>
      );
    /*
     * ⚠️ NEW BUSINESS (migration 335). `undefined` is a server predating it and
     * renders `—`, never a zero nobody measured. The commission is already
     * inside Gross, so it is never added to it again here.
     */
    case 'newBusiness':
      return statement.newBusiness === undefined ? (
        '—'
      ) : (
        <>
          <Money value={statement.newBusiness} currency={statement.currency} />
          <ExcludedMarker count={statement.newBusinessExcludedDocuments ?? 0} />
        </>
      );
    case 'newBusinessCommission':
      return statement.newBusinessCommission === undefined ? (
        '—'
      ) : (
        <Money value={statement.newBusinessCommission} currency={statement.currency} />
      );
    case 'gross':
      return (
        <>
          <Money value={statement.grossPayable} currency={statement.currency} />
          {/*
            * A refund is ADDED to the gross before the clawbacks come off it
            * (migration 235), so it shares the gross cell with a leading `+` —
            * the reader adds it, or the row does not reach Net payable.
            */}
          {hasRefund(statement) && (
            <span className="mt-0.5 block text-xs text-fg-muted tabular">
              +{formatDecimalString(statement.clawbackRefunded)} {CLAWBACK_REFUNDED_SHORT}
            </span>
          )}
        </>
      );
    case 'clawback':
      return (
        <>
          <Money value={statement.clawbackAmount} currency={statement.currency} />
          {/*
            * ⛔ **A SECOND DEDUCTION SHARING THE COLUMN, AND THE LEADING `+` IS
            * WHAT SAYS SO.** The figure above is the `clawbacks` ledger's own sum
            * and this one is not in it, so the two are never added into one
            * number here — but the READER must add them, or the row does not
            * reach Net payable.
            *
            * ⚠️ **Toned MUTED, not warning, unlike the line below it.** This is
            * an ordinary recovery; a second amber line in one cell would make
            * the routine case look like the exceptional one and teach somebody
            * to read past both.
            */}
          {hasBroughtForward(statement) && (
            <span className="mt-0.5 block text-xs text-fg-muted tabular">
              +{formatDecimalString(statement.clawbackBroughtForward)}{' '}
              {CLAWBACK_BROUGHT_FORWARD_SHORT}
            </span>
          )}
          {/*
            * ⚠️ **A PAYOUT IS FLOORED AT ZERO, so a clawback bigger than the
            * year it lands in is capped by that year.** The remainder is kept
            * rather than discarded, and this is the only place it is visible —
            * without it the figure above reads as the whole of what was taken
            * back.
            *
            * 🔴 **IT CARRIES INTO THE NEXT YEAR since migration 227**, so it is
            * not written off — it arrives on a later statement as the `+` line
            * above.
            */}
          {!isZeroDecimal(statement.clawbackUnrecovered) && (
            <span className="mt-0.5 block text-xs text-warning tabular">
              {formatDecimalString(statement.clawbackUnrecovered)} not recovered this year
            </span>
          )}
        </>
      );
    case 'net':
      return <Money value={statement.netPayable} currency={statement.currency} strong />;
    default:
      return null;
  }
}

function StatementsSection({
  rows,
  total,
  initialLoad,
  loading,
  error,
  currencies,
  excluded,
  selectedId,
  onSelect,
  computed,
}: {
  rows: CommissionStatement[];
  total: number;
  initialLoad: boolean;
  loading: boolean;
  error: string | null;
  currencies: string[];
  excluded: { statement: CommissionStatement; scope: string; note: string }[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  computed: boolean;
}) {
  const filters = useTableFilters(rows, STATEMENT_FILTER_COLUMNS, { memoryId: 'statements' });
  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;
  const layout = useColumnLayout(
    'commission-statements',
    STATEMENT_COLUMN_KEYS,
    STATEMENT_LOCKED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, layout.visibleKeys, layout.frozenSideOf);
  const minWidth = useMemo(
    () => minWidthOf(layout.visibleKeys, STATEMENT_COLUMNS),
    [layout.visibleKeys],
  );
  const frozenOf = (key: string) => frozenPropsFor(key, layout.frozenSideOf, frozenOffsets);
  const isNumeric = (key: string) => STATEMENT_COLUMNS[key as StatementColumnKey]?.numeric;

  return (
    <>
      <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
        <p className="m-0 text-sm font-semibold text-fg">Statements</p>
        {/*
          * ⚠️ **THE LEAST OBVIOUS THING ON THIS PAGE, AND IT IS A FACT ABOUT THE
          * DATA.** Crediting an invoice does NOT reduce the revenue behind these
          * figures — a credit note is a separate document and never touches the
          * invoice it credits, so a fully credited invoice still counts at one
          * hundred per cent. The Clawback column is the only thing making the
          * number honest, and without this sentence the natural reading of the
          * revenue is wrong.
          */}
        <NoteLine note={CREDIT_DOES_NOT_REDUCE_REVENUE_NOTE} />

        {/*
          * The excluded-document counts, said in words rather than left to a
          * badge somebody has to hover. See point 4 of the file header: this is
          * the only thing on the screen that can say a commission figure is
          * SHORT.
          */}
        {excluded.length > 0 && (
          <Notice tone="warning" title="Some revenue could not be priced">
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {excluded.map(({ statement, scope, note }) => (
                <li
                  key={`${statement.id}:${scope}`}
                  className="text-xs [overflow-wrap:anywhere]"
                >
                  <span className="font-medium">{leadLabel(statement)}</span>
                  {' · '}
                  {scope}
                  {' — '}
                  {note}
                </li>
              ))}
            </ul>
          </Notice>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-b border-border px-4 py-2">
        <div className="mr-auto md:hidden">
          <FilterSheet
            keys={layout.orderedKeys}
            headerFor={filters.headerFor}
            activeCount={filters.activeCount}
            clearAll={filters.clearAll}
          />
        </div>
        <ColumnManager
          layout={layout}
          labelOf={(key) => STATEMENT_COLUMNS[key as StatementColumnKey]?.label ?? key}
          lockedKeys={STATEMENT_LOCKED_KEYS}
          lockReasonOf={statementLockReason}
        />
      </div>

      {error && (
        <Notice tone="danger" role="alert" className="m-3" title={<>Couldn&rsquo;t load the statements.</>}>
          <span className="[overflow-wrap:anywhere]">{error}</span>
        </Notice>
      )}

      <div
        aria-busy={loading || undefined}
        className={cx(
          'transition-opacity duration-150 ease-out motion-reduce:transition-none',
          loading && rows.length > 0 && 'opacity-60',
        )}
      >
        {/* Below `md`: a card per statement. Nine money and percentage columns
            will not fit 320px, and every one of them is on the card — a figure
            that exists only above a breakpoint is indistinguishable from one
            that was removed. */}
        <div className="md:hidden">
          {initialLoad ? (
            <ul className="m-0 list-none p-0">
              {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <li key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                  <Skeleton className="h-3 w-36" />
                  <Skeleton className="mt-2 h-2.5 w-full" />
                  <Skeleton className="mt-1.5 h-2.5 w-2/3" />
                </li>
              ))}
            </ul>
          ) : rows.length === 0 ? (
            <StatementsEmptyState hasError={error !== null} computed={computed} />
          ) : visibleRows.length === 0 ? (
            <FilteredEmptyState noun="statements" onClear={filters.clearAll} />
          ) : (
            <ul className="m-0 list-none p-0">
              {/* Each labelled figure follows its column's visibility in the
                  column manager, as the table does. */}
              {visibleRows.map((statement) => (
                <li
                  key={statement.id}
                  className={cx(
                    'border-b border-border px-4 py-3 last:border-b-0',
                    statement.id === selectedId && 'bg-surface-sunken',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => onSelect(statement.id === selectedId ? null : statement.id)}
                      className="min-w-0 text-left text-sm font-medium text-fg [overflow-wrap:anywhere] hover:underline"
                    >
                      {leadLabel(statement)}
                    </button>
                    {layout.isVisible('gate') && (
                      <Badge tone={gateTone(statement.gateFactor)}>{gateSummary(statement)}</Badge>
                    )}
                  </div>

                  {/*
                    * ⚠️ **LABELLED, unlike the desktop cells** — that is the
                    * difference between a table and a card rather than an
                    * inconsistency. Five money figures under a heading row mean
                    * something; five figures floating on a card mean nothing. A
                    * label NAMES a value rather than explaining it.
                    */}
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {layout.isVisible('personal') && (
                      <>
                        <dt className="m-0 text-fg-muted">Personal attainment</dt>
                        <dd className="m-0 text-right">
                          <Attainment
                            percent={statement.personalAttainmentPercent}
                            gatePercent={statement.personalGatePercent}
                          />
                        </dd>
                      </>
                    )}
                    {layout.isVisible('regional') && (
                      <>
                        <dt className="m-0 text-fg-muted">Regional attainment</dt>
                        <dd className="m-0 text-right">
                          <Attainment
                            percent={statement.regionalAttainmentPercent}
                            gatePercent={statement.regionalGatePercent}
                          />
                        </dd>
                      </>
                    )}
                    {layout.isVisible('personalCommission') && (
                      <>
                        <dt className="m-0 text-fg-muted">Personal commission</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money
                            value={statement.personalCommission}
                            currency={statement.currency}
                          />
                          {/* Beside the figure it describes — the two counts are
                              never added. */}
                          <ExcludedMarker count={statement.personalExcludedDocuments} />
                        </dd>
                      </>
                    )}
                    {layout.isVisible('regionalPool') && (
                      <>
                        <dt className="m-0 text-fg-muted">Regional pool</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money
                            value={statement.effectiveRegionalPool}
                            currency={statement.currency}
                          />
                          <ExcludedMarker count={statement.regionalExcludedDocuments} />
                        </dd>
                      </>
                    )}
                    {/* `undefined` = a server predating migration 335: the row is
                        left off rather than shown as a zero. */}
                    {layout.isVisible('newBusiness') && statement.newBusiness !== undefined && (
                      <>
                        <dt className="m-0 text-fg-muted">New business</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money value={statement.newBusiness} currency={statement.currency} />
                          <ExcludedMarker count={statement.newBusinessExcludedDocuments ?? 0} />
                        </dd>
                      </>
                    )}
                    {layout.isVisible('newBusinessCommission') &&
                      statement.newBusinessCommission !== undefined && (
                        <>
                          <dt className="m-0 text-fg-muted">New business commission</dt>
                          <dd className="m-0 text-right text-fg-secondary">
                            <Money
                              value={statement.newBusinessCommission}
                              currency={statement.currency}
                            />
                          </dd>
                        </>
                      )}
                    {layout.isVisible('gross') && (
                      <>
                        <dt className="m-0 text-fg-muted">Gross</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money value={statement.grossPayable} currency={statement.currency} />
                        </dd>
                      </>
                    )}
                    {/*
                      * An ADDITION, before the deductions: the server adds a
                      * refund to the gross before it subtracts the clawbacks
                      * (migration 235), so the rows read in that order add up.
                      */}
                    {/* The refund rides with Gross, as it shares the gross cell
                        on the table. */}
                    {layout.isVisible('gross') && hasRefund(statement) && (
                      <>
                        <dt className="m-0 text-fg-muted">{CLAWBACK_REFUNDED_LABEL}</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money
                            value={statement.clawbackRefunded}
                            currency={statement.currency}
                          />
                        </dd>
                      </>
                    )}
                    {layout.isVisible('clawback') && (
                      <>
                        <dt className="m-0 text-fg-muted">Clawback</dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money value={statement.clawbackAmount} currency={statement.currency} />
                        </dd>
                      </>
                    )}
                    {/*
                      * A SECOND DEDUCTION, never added to the one above: that
                      * figure is the `clawbacks` ledger's own sum. Without this
                      * pair the rows do not add up to Net payable and nothing on
                      * screen says why — which reads as an arithmetic fault on a
                      * payslip rather than as a recovery from an earlier year.
                      */}
                    {layout.isVisible('clawback') && hasBroughtForward(statement) && (
                      <>
                        <dt className="m-0 text-fg-muted">
                          {CLAWBACK_BROUGHT_FORWARD_LABEL}
                        </dt>
                        <dd className="m-0 text-right text-fg-secondary">
                          <Money
                            value={statement.clawbackBroughtForward}
                            currency={statement.currency}
                          />
                        </dd>
                      </>
                    )}
                    {layout.isVisible('net') && (
                      <>
                        <dt className="m-0 text-fg-muted">Net payable</dt>
                        <dd className="m-0 text-right">
                          <Money value={statement.netPayable} currency={statement.currency} strong />
                        </dd>
                      </>
                    )}
                  </dl>

                  {layout.isVisible('clawback') && !isZeroDecimal(statement.clawbackUnrecovered) && (
                    <p className="mt-1.5 m-0 text-xs text-warning tabular">
                      {formatDecimalString(statement.clawbackUnrecovered)} of the clawback could
                      not be recovered this year
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* From `md`: the table. */}
        <div className="hidden md:block">
          {!initialLoad && rows.length === 0 ? (
            <StatementsEmptyState hasError={error !== null} computed={computed} />
          ) : (
            <>
            <TableWrap frozenHeader>
              <Table style={{ minWidth }}>
                <THead>
                  {/* Measured for frozen offsets; stays mounted when the filters
                      empty the table, so the funnel is still there to undo. */}
                  <TR ref={headRowRef}>
                    {layout.visibleKeys.map((key) => (
                      <FilterableTH key={key} {...filters.headerFor(key)} wrap {...frozenOf(key)} />
                    ))}
                  </TR>
                </THead>
                {initialLoad ? (
                  <TBody>
                    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                      <TR key={index}>
                        {layout.visibleKeys.map((key) => (
                          <TD key={key} numeric={isNumeric(key)} {...frozenOf(key)}>
                            <Skeleton className={cx('h-3 w-20', isNumeric(key) && 'ml-auto')} />
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </TBody>
                ) : (
                  <TBody>
                    {visibleRows.map((statement) => (
                      <TR
                        key={statement.id}
                        interactive
                        className={cx(statement.id === selectedId && 'bg-surface-sunken')}
                      >
                        {layout.visibleKeys.map((key) => (
                          <TD
                            key={key}
                            numeric={isNumeric(key)}
                            verticalAlign="top"
                            {...frozenOf(key)}
                          >
                            <StatementCell
                              column={key}
                              statement={statement}
                              selected={statement.id === selectedId}
                              onSelect={onSelect}
                            />
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </TBody>
                )}
              </Table>
            </TableWrap>
            {!initialLoad && visibleRows.length === 0 && (
              <FilteredEmptyState noun="statements" onClear={filters.clearAll} />
            )}
            </>
          )}
        </div>
      </div>

      {/* Only once there is something to count. An empty run says so in its
          empty state. */}
      {total > 0 && (
        <PanelFooter>
          <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
            {`Showing ${rows.length} of ${total}`}
            {hiddenByFilters > 0 && (
              <span className="text-fg-subtle"> · {hiddenByFilters} hidden by column filters</span>
            )}
            {/*
              * ⛔ **NO COLUMN TOTAL, AND IT IS NOT A GAP.** Each statement
              * carries its own frozen currency, so a sum across a run holding
              * two codes would be wrong by construction. The count is the real
              * number.
              */}
            {currencies.length > 1 && (
              <span className="ml-1.5 text-fg-subtle">
                · figures are in {currencies.join(', ')} and are not added together
              </span>
            )}
          </p>
        </PanelFooter>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * Creating a run
 * -------------------------------------------------------------------------- */

/**
 * A run names a PERIOD and a name, and nothing else — every figure on it is
 * computed rather than typed.
 *
 * ⚠️ **The period picker reads the master rather than the loaded runs**, so a
 * year nobody has run yet is offerable. A vocabulary built from the rows on
 * screen can only ever offer the years somebody has already done.
 */
function CreateRunModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [periods, setPeriods] = useState<TargetPeriodRecord[] | null>(null);
  const [periodsFailed, setPeriodsFailed] = useState(false);
  const [periodId, setPeriodId] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const params: TargetPeriodListParams = {
      page: 1,
      perPage: PERIOD_PER_PAGE,
      sort: 'startDate',
      order: 'desc',
    };
    api
      .listTargetPeriods(params, controller.signal)
      .then((answer) => {
        setPeriods(answer.data);
        setPeriodsFailed(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        // An empty list and a failed read look identical in a dropdown, so the
        // failure is said in words beside it.
        setPeriods([]);
        setPeriodsFailed(caught instanceof Error);
      });
    return () => controller.abort();
  }, []);

  const options = useMemo(
    () =>
      (periods ?? []).map((period) => ({
        value: String(period.id),
        label: period.name,
        hint: `${formatDateOnly(period.startDate)} – ${formatDateOnly(period.endDate)}`,
      })),
    [periods],
  );

  const submit = useCallback(async () => {
    const id = Number(periodId);
    if (!Number.isInteger(id) || id <= 0 || name.trim() === '') return;
    setSaving(true);
    setError(null);
    try {
      const answer = await api.createCommissionRun({
        periodId: id,
        name: name.trim(),
        // Omitted rather than sent empty: an empty string is a value, and this
        // column is nullable precisely so "no note" can be expressed.
        ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
      });
      onCreated(answer.data.id);
    } catch (caught: unknown) {
      setError(messageOf(caught, 'The run could not be created'));
    } finally {
      setSaving(false);
    }
  }, [periodId, name, notes, onCreated]);

  const ready = periodId !== '' && name.trim() !== '';

  return (
    <Modal
      open
      onClose={onClose}
      title="New commission run"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} loading={saving} disabled={!ready}>
            Create run
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && (
          <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
            {error}
          </Notice>
        )}

        <Field
          label="Period"
          required
          {...(periodsFailed ? { error: 'Couldn’t load the periods.' } : {})}
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={periodId}
              onChange={setPeriodId}
              options={options}
              placeholder="Choose a period"
              searchPlaceholder="Search periods…"
              loading={periods === null}
              aria-describedby={describedBy}
              {...(periodsFailed ? { invalid: true } : {})}
            />
          )}
        </Field>

        <Field label="Name" required>
          {({ id }) => (
            <Input
              id={id}
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Notes">
          {({ id }) => (
            <Textarea
              id={id}
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- *
 * Empty states
 * -------------------------------------------------------------------------- */

/**
 * ⛔ **NO COPY HERE IMPLIES DATA IS ON ITS WAY.** A blank list is an ordinary,
 * permanent state in this app, and an empty state that hedges about it is how a
 * dead endpoint gets mistaken for a quiet week — `GET /deliverables` returned a
 * `500` for a day and rendered exactly its documented empty state.
 */
function RunsEmptyState({ hasError, denial }: { hasError: boolean; denial: string | null }) {
  if (denial !== null) {
    /*
     * The refusal in the server's terms rather than "administrators only",
     * which has been the wrong reason more than once. The READ is genuinely
     * refused here — migration 205 grants this module to two administrator sets
     * and to nobody else — so this is not a hidden page, it is a closed door.
     */
    return <EmptyState title="Nothing to show" description={denial} />;
  }
  if (hasError) {
    return (
      <EmptyState
        title="Nothing to show"
        description="The runs couldn’t be loaded. Check the message above and try again."
      />
    );
  }
  return <EmptyState title="No commission runs yet" description="Nothing has been run." />;
}

/**
 * The list loaded and the column filters hid every row — distinct from an empty
 * list, which is its own empty state. The house wording (`CyclesPage`).
 */
function FilteredEmptyState({ noun, onClear }: { noun: string; onClear: () => void }) {
  return (
    <EmptyState
      title={`No ${noun} match the column filters`}
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear column filters
        </Button>
      }
    />
  );
}

function StatementsEmptyState({
  hasError,
  computed,
}: {
  hasError: boolean;
  computed: boolean;
}) {
  if (hasError) {
    return (
      <EmptyState
        title="Nothing to show"
        description="The statements couldn’t be loaded. Check the message above and try again."
      />
    );
  }
  return (
    <EmptyState
      title={computed ? 'No statements' : 'Not computed yet'}
      description={
        computed
          ? 'This run produced no statements. Anything that could not be computed is listed above.'
          : 'Compute the run to produce a statement for each assignment.'
      }
    />
  );
}
